/**
 * Quote Request System
 * @version 2.3.1 - 2026-09-29 - DOM-Daten pro Warenkorbzeile statt pro Variante
 *   (doppelte Variante im Warenkorb überschrieb die Zeilensumme, OF-2026-8521)
 * @version 2.3.0 - 2026-02-13 - Metaobject discount system support
 *
 * Architecture:
 * - QuoteRequestModal class handles modal open/close, accessibility
 * - Form validation, submission, and cart data collection
 * - Event delegation for button clicks
 *
 * Discount system:
 * - Metaobject-based: discounts are pre-rendered in Liquid
 *   -> Reads data-eigenmontage-pct / data-montage-pct from cart items
 *   -> No race conditions, data is in HTML from server render
 *
 * Patterns from lead-form-advanced.liquid:
 * - Honeypot spam protection
 * - Time-based anti-bot (3 second delay)
 * - UX-first: show success immediately, webhook fires in background
 */
console.log('[QuoteRequest] Version 2.3.1 loaded');

class QuoteRequestModal {
  constructor() {
    console.log('[QuoteRequest] Constructor called');

    this.modal = document.getElementById('quote-request-modal');
    this.overlay = document.getElementById('quote-request-overlay');

    console.log('[QuoteRequest] Modal found:', !!this.modal);

    if (!this.modal) return;

    this.closeBtn = this.modal.querySelector('.quote-request-modal__close');
    this.form = document.getElementById('quote-request-form');
    this.contentContainer = document.getElementById('quote-request-content');
    this.cartItemsContainer = document.getElementById('quote-request-cart-items');

    this.startTime = Date.now();
    this.isSubmitting = false;
    this.boundHandleSubmit = (e) => this.handleSubmit(e);
    this.cartData = null;
    this.originalContent = this.contentContainer?.innerHTML || '';
    this.init();
  }

  init() {
    // Close button
    this.closeBtn?.addEventListener('click', () => this.close());

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Delegate button clicks (works for dynamically added buttons)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quote-request-trigger]');
      if (trigger) {
        e.preventDefault();
        this.open();
      }
    });

    // Form submission
    this.form?.addEventListener('submit', this.boundHandleSubmit);
  }

  isOpen() {
    return this.modal?.classList.contains('is-active');
  }

  async open() {
    console.log('[QuoteRequest] open() called');

    // Show modal and overlay immediately
    this.modal.classList.add('is-active');
    this.overlay.classList.add('is-active');
    document.body.classList.add('quote-modal-open');

    // Re-query DOM elements
    this.cartItemsContainer = document.getElementById('quote-request-cart-items');
    this.form = document.getElementById('quote-request-form');

    console.log('[QuoteRequest] cartItemsContainer found:', !!this.cartItemsContainer);

    // If cart container doesn't exist (cart was empty at page load), create it
    if (!this.cartItemsContainer) {
      const cartSection = this.modal.querySelector('.quote-request-cart');
      console.log('[QuoteRequest] cartSection found:', !!cartSection);

      if (cartSection) {
        // Remove empty state div if it exists
        const emptyState = cartSection.querySelector('.quote-request-cart__empty');
        console.log('[QuoteRequest] emptyState found:', !!emptyState);

        if (emptyState) {
          emptyState.remove();
          console.log('[QuoteRequest] emptyState removed');
        }

        // Create the cart items container
        const ul = document.createElement('ul');
        ul.id = 'quote-request-cart-items';
        ul.className = 'quote-request-cart__items mini-cart cart_list product_list_widget';

        // Insert after the title
        const title = cartSection.querySelector('.quote-request-cart__title');
        if (title) {
          title.after(ul);
        } else {
          cartSection.prepend(ul);
        }
        this.cartItemsContainer = ul;
        console.log('[QuoteRequest] Created new cartItemsContainer');

        // Also create totals container if it doesn't exist
        let totalsContainer = cartSection.querySelector('.quote-request-cart__totals');
        if (!totalsContainer) {
          totalsContainer = document.createElement('div');
          totalsContainer.className = 'quote-request-cart__totals';
          cartSection.appendChild(totalsContainer);
        }
      } else {
        console.error('[QuoteRequest] .quote-request-cart not found in modal!');
      }
    }

    // Show loading spinner in cart area
    if (this.cartItemsContainer) {
      this.cartItemsContainer.innerHTML = `
        <li class="quote-request-cart__loading">
          <div class="quote-request-spinner"></div>
          <p>${(window.__QR_I18N&&window.__QR_I18N.loading)||'Warenkorb wird geladen...'}</p>
        </li>
      `;
      console.log('[QuoteRequest] Loading spinner displayed');
    } else {
      console.error('[QuoteRequest] cartItemsContainer is still null!');
    }

    // Clear totals during loading
    const totalsContainer = this.modal.querySelector('.quote-request-cart__totals');
    if (totalsContainer) {
      totalsContainer.innerHTML = '';
    }

    // Disable submit button during loading
    const submitBtn = this.form?.querySelector('.quote-request-form__submit');
    submitBtn?.setAttribute('disabled', 'disabled');

    // Reset form state
    this.form?.reset();
    this.startTime = Date.now();
    this.isSubmitting = false;

    // Fetch cart data in background
    await this.loadCartData();

    // Remove loading state from submit button (if cart has items)
    if (this.cartData && this.cartData.item_count > 0) {
      submitBtn?.classList.remove('is-loading');
      submitBtn?.removeAttribute('disabled');

      // Show debug output if enabled (before form submission)
      const isDebugMode = this.modal.dataset.debug === 'true';
      if (isDebugMode) {
        await this.showPreSubmitDebug();
      }
    }

    // Focus first input for accessibility
    setTimeout(() => {
      const firstInput = this.form?.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
      firstInput?.focus();
    }, 100);
  }

  close() {
    this.modal.classList.remove('is-active');
    this.overlay.classList.remove('is-active');
    document.body.classList.remove('quote-modal-open');

    // Reset to original content (removes success state)
    if (this.contentContainer && this.originalContent) {
      this.contentContainer.innerHTML = this.originalContent;
      // Re-bind form reference since we replaced the DOM
      this.form = document.getElementById('quote-request-form');
      this.form?.addEventListener('submit', this.boundHandleSubmit);
    }
  }

  async loadCartData() {
    try {
      // Render cart section from live cart drawer DOM (source of truth)
      // This also fetches fresh /cart.js data at the end for payload
      await this.fetchAndRenderCartSection();
    } catch (error) {
      console.error('Quote Request: Failed to load cart data', error);
    }
  }

  async fetchAndRenderCartSection() {
    console.log('[QuoteRequest] fetchAndRenderCartSection() called');

    try {
      // Re-query DOM elements (they may have been recreated after modal close/reopen)
      this.cartItemsContainer = document.getElementById('quote-request-cart-items');
      console.log('[QuoteRequest] Re-queried cartItemsContainer:', !!this.cartItemsContainer);

      // ALWAYS trigger cart drawer refresh first - don't rely on cached /cart.js data
      console.log('[QuoteRequest] Triggering cart drawer refresh...');
      await this.triggerCartDrawerRefresh();
      console.log('[QuoteRequest] Cart drawer refresh complete');

      // Discounts are pre-rendered in Liquid via metaobject system, no JS wait needed
      console.log('[QuoteRequest] Discounts pre-rendered in Liquid - no wait needed');

      // Then: Copy from the LIVE DOM (which includes JS-applied discounts)
      const cartDrawer = document.getElementById('Cart-Drawer');
      console.log('[QuoteRequest] Cart drawer found:', !!cartDrawer);

      if (!cartDrawer) {
        console.warn('Quote Request: Cart drawer not found');
        this.updateEmptyCartState();
        return;
      }

      // Extract cart items from live DOM
      const cartItems = cartDrawer.querySelector('ul.mini-cart.cart_list');
      console.log('[QuoteRequest] cartItems UL found:', !!cartItems);

      // Check if cart drawer actually has items (ignore cart-empty state)
      const itemCount = cartItems ? cartItems.querySelectorAll('.mini_cart_item, li[data-variant-id]').length : 0;
      const hasItems = itemCount > 0;
      console.log('[QuoteRequest] Items found in cart drawer:', itemCount);

      if (!hasItems) {
        // Cart drawer is empty - show empty state
        this.cartData = { item_count: 0 }; // Update local state
        this.updateEmptyCartState();
        return;
      }

      if (cartItems && this.cartItemsContainer) {
        // Clone the items
        const clone = cartItems.cloneNode(true);

        // Remove elements we don't want in the quote modal
        clone.querySelectorAll('.free-shipping, .sky-upsell').forEach(el => el.remove());

        this.cartItemsContainer.innerHTML = clone.innerHTML;

        // Remove quantity controls (modal is read-only)
        this.cartItemsContainer.querySelectorAll('.thb-quantity-container, .mini-cart-remove, .remove').forEach(el => el.remove());
      }

      // Extract totals from live DOM
      const totalsContainer = this.modal.querySelector('.quote-request-cart__totals');
      if (totalsContainer) {
        let totalsHtml = '';

        // Copy cart-level discounts
        cartDrawer.querySelectorAll('.mini-cart__discount').forEach(discount => {
          totalsHtml += discount.outerHTML;
        });

        // Copy subtotal row
        const subtotalRow = cartDrawer.querySelector('#cart-drawer-subtotal');
        if (subtotalRow) {
          totalsHtml += subtotalRow.outerHTML;
        }

        if (totalsHtml) {
          totalsContainer.innerHTML = totalsHtml;
        }
      }

      // Re-fetch cart data for accurate payload (needed for form submission)
      const response = await fetch('/cart.js?t=' + Date.now());
      this.cartData = await response.json();

      // Enable submit button since we have items
      const submitBtn = this.form?.querySelector('.quote-request-form__submit');
      if (submitBtn) {
        submitBtn.removeAttribute('disabled');
      }

    } catch (error) {
      console.error('Quote Request: Failed to render cart section', error);
    }
  }

  triggerCartDrawerRefresh() {
    // Fetch the authoritative cart-drawer markup directly via the Section Rendering
    // API and swap it into the live drawer. This replaces the old approach of
    // dispatching `cart:refresh` and waiting for `cart:refresh:complete` — that event
    // is only dispatched by the cart-page `Cart` class (cart.min.js), so on product/
    // collection/home pages it never fired and the popup always ate the full 3000ms
    // fallback timeout. The fetch resolves deterministically in ~200-400ms with the
    // same server-rendered HTML (discount attributes baked in), independent of any
    // Cart instance. On failure we fall through and read whatever live DOM exists.
    return fetch('/cart.js?sections=cart-drawer&sections_url=' + encodeURIComponent(window.location.pathname))
      .then((r) => r.json())
      .then((data) => {
        const html = data && (data['cart-drawer'] || (data.sections && data.sections['cart-drawer']));
        if (!html) return;
        const drawer = document.getElementById('Cart-Drawer');
        if (!drawer) return;
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const newSpc = temp.querySelector('.side-panel-content');
        const curSpc = drawer.querySelector('.side-panel-content');
        if (newSpc && curSpc) {
          // Replace only the inner content, never the <aside> wrapper.
          curSpc.innerHTML = newSpc.innerHTML;
          // Re-apply JS discount display (active assembly button) on the fresh rows.
          if (window.applyCartDrawerDiscounts) {
            try { window.applyCartDrawerDiscounts(); } catch (e) {}
          }
        }
      })
      .catch(() => { /* fall through: read existing live DOM (= old behavior minus 3s wait) */ });
  }

  updateEmptyCartState() {
    // Re-query form (it may have been recreated after modal close/reopen)
    this.form = document.getElementById('quote-request-form');

    if (!this.cartData || this.cartData.item_count === 0) {
      if (this.cartItemsContainer) {
        this.cartItemsContainer.innerHTML = `
          <li class="quote-request-cart__empty">
            <p>${(window.__QR_I18N&&window.__QR_I18N.empty)||'Ihr Warenkorb ist leer.'}</p>
            <p>${(window.__QR_I18N&&window.__QR_I18N.emptyHint)||'Bitte füge zuerst Produkte hinzu, um eine Offerte anzufordern.'}</p>
          </li>
        `;
      }
      const submitBtn = this.form?.querySelector('.quote-request-form__submit');
      if (submitBtn) {
        submitBtn.setAttribute('disabled', 'disabled');
      }
    } else {
      const submitBtn = this.form?.querySelector('.quote-request-form__submit');
      if (submitBtn) {
        submitBtn.removeAttribute('disabled');
      }
    }
  }


  async handleSubmit(e) {
    e.preventDefault();

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const submitBtn = this.form.querySelector('.quote-request-form__submit');

    // Honeypot check
    const honeypot = this.form.querySelector('[name="company"]');
    if (honeypot && honeypot.value !== '') {
      // Bot detected - silently fail
      this.isSubmitting = false;
      return;
    }

    // Time-based anti-bot (3 second minimum)
    if ((Date.now() - this.startTime) < 3000) {
      // Too fast - likely a bot
      this.isSubmitting = false;
      return;
    }


    /* Telefonnummer muss echt sein: mindestens 9 Ziffern, egal in welcher
       Schreibweise (079 ..., +41 79 ...). Dem Pflichtfeld allein genuegte
       ein Strich oder ein Leerzeichen - im Backend kam das als leere Nummer
       an und der Verkauf erreichte den Kunden nicht (05.10.2026). */
    var telFeld = this.form.querySelector('[name="phone"]');
    if (telFeld) {
      var telZiffern = String(telFeld.value || '').replace(/[^0-9]/g, '').length;
      telFeld.setCustomValidity(telZiffern >= 9
        ? ''
        : 'Bitte gib eine vollständige Telefonnummer an (mindestens 9 Ziffern).');
    }

    // Validate form
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      this.isSubmitting = false;
      return;
    }

    // Show loading state
    submitBtn?.classList.add('is-loading');
    submitBtn?.setAttribute('disabled', 'disabled');

    // Collect form data
    const formData = Object.fromEntries(new FormData(this.form));
    delete formData.company; // Remove honeypot from payload

    // Refresh cart data directly from /cart.js
    // NOTE: We intentionally do NOT call loadCartData() here because it triggers
    // a cart drawer DOM refresh which can cause a race condition: the refresh
    // may cause Shopify to recalculate automatic discounts, and the subsequent
    // /cart.js fetch can return data BEFORE recalculation completes, resulting
    // in missing discount names/amounts in the payload.
    try {
      const cartResponse = await fetch('/cart.js?t=' + Date.now());
      if (cartResponse.ok) {
        this.cartData = await cartResponse.json();
      }
    } catch (e) {
      console.warn('[QuoteRequest] Cart refresh failed, using existing data');
    }

    // Guard: leerer Warenkorb -> kein Webhook, Hinweis anzeigen
    if (!this.cartData || !this.cartData.item_count || !Array.isArray(this.cartData.items) || this.cartData.items.length === 0) {
      submitBtn?.classList.remove('is-loading');
      this.isSubmitting = false;
      this.updateEmptyCartState();
      return;
    }

    // Build payload (async - fetches missing barcodes if needed)
    const payload = await this.buildPayload(formData, this.cartData);

    // ============ DEBUG OUTPUT ============
    const isDebugMode = this.modal.dataset.debug === 'true';
    if (isDebugMode) {
      console.group('📤 QUOTE REQUEST DEBUG (Final Payload with Customer)');
      console.log('Final payload:', JSON.parse(JSON.stringify(payload)));
      console.groupEnd();
    }
    // ============ END DEBUG ============

    // Show success immediately (UX first)
    this.showSuccess();

    // Messung: das Formular schickt per fetch, ein natives submit-Event gibt es
    // nicht — der GTM-Trigger «Formular abgeschickt» griff darum nur bei ~15 % der
    // Anfragen (Audit 04.09.2026). Erfolgs-Event fuer GTM/Meta + Google-Ads-
    // Conversion «Kontaktformular (GTM) - skypergola.ch» direkt via gtag.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'quote_request_submitted', form_id: 'quote-request-form' });
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: 'AW-690004701/4vKuCKDYlIsZEN3FgskC' });
      }
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: 'quote_request' });
    } catch (_) {}

    // Send webhook in background
    this.sendWebhook(payload);
  }

  async buildPayload(formData, cart) {
    // Build a map of variant_id -> DOM data (barcode, discounts from Liquid)
    let domDataMap = this.extractDomData();

    // Fetch missing barcodes from product JSON API (for dynamic cart updates)
    domDataMap = await this.fetchMissingBarcodes(cart.items, domDataMap);

    // Build items with DOM-enriched discount data
    // DOM-Daten pro Warenkorbzeile, nicht pro Variante: dieselbe Variante kann mehrfach
    // im Warenkorb liegen (z.B. «Montage Zubehör – 800» für die 6m- und die 5m-Glaswand).
    const usedDomLines = new Set();
    const items = cart.items.map((item, idx) => {
      const domData = this.domDataForCartLine(domDataMap, item, idx, usedDomLines);

      // Get barcode from DOM (Liquid-rendered) since /cart.js doesn't provide it
      const barcode = domData.barcode || '';

      // Get prices from DOM first (matches displayed values), fallback to cart.js
      const originalLinePrice = domData.originalLinePrice || item.original_line_price;
      const finalLinePrice = domData.finalLinePrice || item.final_line_price;

      // Calculate discount amount - prioritize DOM data to ensure payload matches display
      const discountAmount = domData.discountAmount || (originalLinePrice - finalLinePrice) || 0;

      // Get discounts array - prioritize DOM data to ensure payload matches displayed values
      let discounts = [];

      // First: Use DOM discount titles (extracted from visible discount pills)
      // This is the most reliable source as it matches exactly what user sees
      if (domData.discountTitles?.length > 0 && discountAmount > 0) {
        // Distribute the discount amount across all titles
        const amountPerDiscount = Math.round(discountAmount / domData.discountTitles.length);
        discounts = domData.discountTitles.map(title => ({
          title: title,
          amount: amountPerDiscount
        }));
      }

      // Fallback: Try DOM discountsJson (but often has errors from Liquid)
      if (discounts.length === 0 && domData.discountsJson) {
        try {
          const liquidDiscounts = JSON.parse(domData.discountsJson);
          // Check it's not an error response
          if (Array.isArray(liquidDiscounts) && liquidDiscounts.length > 0 && !liquidDiscounts[0]?.error) {
            discounts = liquidDiscounts.map(d => ({
              title: d.discount_application?.title || d.title || 'Rabatt',
              amount: d.amount || 0
            }));
          }
        } catch (e) {
          // Silently ignore parse errors - expected for Shopify discount objects
        }
      }

      // Fallback: cart.js item.discounts
      if (discounts.length === 0 && item.discounts?.length > 0) {
        discounts = item.discounts.map(d => ({
          title: d.title || 'Rabatt',
          amount: d.amount || 0
        }));
      }

      // Fallback: cart.js line_level_discount_allocations
      if (discounts.length === 0 && item.line_level_discount_allocations?.length > 0) {
        discounts = item.line_level_discount_allocations.map(d => ({
          title: d.discount_application?.title || 'Rabatt',
          amount: d.amount || 0
        }));
      }

      // If we still have no discounts array but there IS a discount, create one from the calculated amount
      if (discounts.length === 0 && discountAmount > 0) {
        discounts = [{
          title: 'Rabatt',
          amount: discountAmount
        }];
      }

      // Ensure discount amounts are populated (fix for amount: 0 issue)
      if (discounts.length > 0 && discounts.every(d => d.amount === 0) && discountAmount > 0) {
        // All discount amounts are 0 but we have a calculated discount - distribute it
        const amountPerDiscount = Math.round(discountAmount / discounts.length);
        discounts = discounts.map(d => ({
          ...d,
          amount: amountPerDiscount
        }));
      }

      return {
        id: item.id,
        key: item.key,
        variant_id: item.variant_id,
        product_id: item.product_id,
        sku: domData.sku || item.sku || '',
        barcode: barcode,
        sort_priority: domData.sortPriority || '',
        title: item.product_title,
        variant_title: item.variant_title,
        product_type: item.product_type,
        vendor: item.vendor,
        handle: item.handle,
        url: item.url,
        image_url: item.image,
        quantity: item.quantity,
        price: item.price,
        original_price: item.original_price,
        final_price: item.final_price,
        line_price: item.line_price,
        original_line_price: originalLinePrice,
        final_line_price: finalLinePrice,
        discount_amount: discountAmount,
        discounts: discounts,
        options: item.options_with_values || [],
        properties: item.properties || {}
      };
    });

    // Recalculate totals from DOM-enriched items (may differ from cart.js)
    const totalOriginal = items.reduce((sum, item) => sum + item.original_line_price, 0);
    const totalFinal = items.reduce((sum, item) => sum + item.final_line_price, 0);
    const totalDiscount = totalOriginal - totalFinal;
    const useCorrectedTotals = totalDiscount > cart.total_discount;
    const correctedTotalDiscount = useCorrectedTotals ? totalDiscount : cart.total_discount;
    const correctedTotalPrice = useCorrectedTotals
      ? (cart.original_total_price - totalDiscount)
      : cart.total_price;

    // Aggregate discount codes: cart.js codes + item-level discount titles from DOM
    const discountCodes = this.buildDiscountCodes(cart);
    items.forEach(item => {
      item.discounts.forEach(d => {
        if (d.title && d.amount > 0 &&
            !discountCodes.some(c => c.code.toUpperCase() === d.title.toUpperCase())) {
          discountCodes.push({ code: d.title, amount: d.amount, source: 'item_discount' });
        }
      });
    });

    return {
      timestamp: new Date().toISOString(),
      source: window.location.href,
      shop: {
        name: window.Shopify?.shop || document.title,
        domain: window.location.hostname,
        currency: cart.currency || window.Shopify?.currency?.active || window.shop_currency || 'CHF'
      },
      customer: {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || '',
        address: {
          street: formData.street || '',
          zip: formData.zip || '',
          city: formData.city || ''
        },
        availability: formData.availability || '',
        remarks: formData.remarks || ''
      },
      cart: {
        token: cart.token,
        item_count: cart.item_count,
        original_total_price: cart.original_total_price,
        total_price: correctedTotalPrice,
        total_discount: correctedTotalDiscount,
        currency: cart.currency,
        items: items
      },
      discounts: {
        codes: discountCodes,
        cart_level: cart.cart_level_discount_applications?.map(d => ({
          title: d.title,
          amount: d.total_allocated_amount
        })) || [],
        automatic: this.aggregateAutomaticDiscounts(cart)
      },
      formatted: {
        subtotal: this.formatMoney(cart.original_total_price),
        total: this.formatMoney(correctedTotalPrice),
        total_discount: this.formatMoney(correctedTotalDiscount)
      }
    };
  }

  /**
   * Extract data from DOM that's only available via Liquid (barcode, etc.)
   * Returns a map of variant_id -> { barcode, sku, discountAmount, discountsJson }
   *
   * Reads data-eigenmontage-pct/data-montage-pct + assemblyPreference
   * to calculate discounts. No race conditions, data is in HTML from server render.
   */
  extractDomData() {
    const map = {};
    const lines = [];
    const cartDrawer = document.getElementById('Cart-Drawer');
    const items = cartDrawer?.querySelectorAll('[data-variant-id]') || [];

    // Determine which button is active (1 = Eigenmontage, 2 = Montage)
    const pref = localStorage.getItem('assemblyPreference') || '1';
    const pctAttr = pref === '2' ? 'data-montage-pct' : 'data-eigenmontage-pct';
    const codeAttr = pref === '2' ? 'data-montage-code' : 'data-eigenmontage-code';

    items.forEach(el => {
      const variantId = el.dataset.variantId;
      if (!variantId) return;

      const pct = parseFloat(el.getAttribute(pctAttr) || '0');
      const code = el.getAttribute(codeAttr) || '';

      // Get quantity
      const qtyEl = el.querySelector('.mini-quantity');
      const qty = qtyEl ? parseInt(qtyEl.textContent.replace(/[^0-9]/g, '')) || 1 : 1;

      // data-original-price = per-unit price (item.price)
      // data-original-line-price = line total (item.original_line_price, already includes qty)
      const perUnitPrice = parseInt(el.dataset.originalPrice || '0', 10);
      const lineOriginal = perUnitPrice > 0
        ? perUnitPrice * qty
        : parseInt(el.dataset.originalLinePrice || '0', 10);

      // Calculate discount from percentage
      let discountAmount = 0;
      let finalLinePrice = lineOriginal;

      if (pct > 0 && lineOriginal > 0) {
        discountAmount = Math.round(lineOriginal * (pct / 100));
        finalLinePrice = lineOriginal - discountAmount;
      }

      // Extract discount titles from visible pills
      const discountTitles = [];
      el.querySelectorAll('.custom-discount-pill, .selected-applied-discount').forEach(pill => {
        const text = pill.textContent?.trim();
        if (text && !discountTitles.includes(text)) {
          discountTitles.push(text);
        }
      });

      // If code is set but no pill found, use the code as the title
      if (discountTitles.length === 0 && code && discountAmount > 0) {
        discountTitles.push(code);
      }

      const entry = {
        barcode: el.dataset.barcode || '',
        sku: el.dataset.sku || '',
        discountAmount: discountAmount,
        discountsJson: '',
        discountTitles: discountTitles,
        sortPriority: el.dataset.sortPriority || '',
        originalLinePrice: lineOriginal,
        finalLinePrice: finalLinePrice
      };
      map[variantId] = entry;

      // Zusätzlich jede Drawer-Zeile einzeln merken. Pro Variante überschreibt die spätere
      // Zeile die frühere; bei doppelter Variante ging so die falsche Zeilensumme raus
      // (OF-2026-8521: 3 × 800 als CHF 800 statt 2'400).
      if (el.classList.contains('mini_cart_item')) {
        const idMatch = (el.id || '').match(/^CartDrawerItem-(\d+)$/);
        lines.push(Object.assign({}, entry, {
          variantId: String(variantId),
          qty: qty,
          lineKey: el.querySelector('[data-key]')?.dataset.key || '',
          lineIndex: idMatch ? parseInt(idMatch[1], 10) - 1 : -1
        }));
      }
    });

    // Nicht aufzählbar: fetchMissingBarcodes() sieht die Map weiterhin nur pro Variante
    Object.defineProperty(map, '__lines', { value: lines, enumerable: false });
    return map;
  }

  /**
   * DOM-Daten für genau eine Warenkorbzeile (cart.js-Item an Position idx).
   * Liegt die Variante mehrfach im Warenkorb, wird die Drawer-Zeile über Zeilen-Key,
   * Zeilenindex oder Menge zugeordnet. Ohne sicheren Treffer gelten die Preise aus
   * /cart.js; Barcode und SKU kommen weiter aus der Varianten-Map.
   */
  domDataForCartLine(domDataMap, item, idx, usedLines) {
    const variantData = domDataMap[item.variant_id] || {};
    const lines = (domDataMap.__lines || []).filter(l => l.variantId === String(item.variant_id));
    if (lines.length === 0) return variantData;

    const frei = l => !usedLines.has(l) && l.qty === item.quantity;
    const line =
      lines.find(l => frei(l) && l.lineKey && l.lineKey === item.key) ||
      lines.find(l => frei(l) && l.lineIndex === idx) ||
      lines.find(frei);

    if (line) {
      usedLines.add(line);
      return Object.assign({}, line, {
        barcode: variantData.barcode || line.barcode,
        sku: variantData.sku || line.sku
      });
    }
    if (lines.length === 1) return variantData;
    return {
      barcode: variantData.barcode || '',
      sku: variantData.sku || '',
      sortPriority: variantData.sortPriority || ''
    };
  }

  /**
   * Fetch barcode data from product JSON API for items missing barcode
   * This is needed when cart was updated dynamically without page reload
   */
  async fetchMissingBarcodes(cartItems, domDataMap) {
    const itemsNeedingBarcode = cartItems.filter(item => {
      const domData = domDataMap[item.variant_id];
      return !domData?.barcode;
    });

    if (itemsNeedingBarcode.length === 0) {
      return domDataMap;
    }

    // Group by handle to minimize API calls
    const handleMap = {};
    itemsNeedingBarcode.forEach(item => {
      if (!handleMap[item.handle]) {
        handleMap[item.handle] = [];
      }
      handleMap[item.handle].push(item.variant_id);
    });

    // Fetch product data for each unique handle
    const fetchPromises = Object.keys(handleMap).map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.json`);
        if (!response.ok) return null;
        const data = await response.json();
        return data.product;
      } catch (e) {
        console.warn(`[QuoteRequest] Failed to fetch product ${handle}:`, e);
        return null;
      }
    });

    const products = await Promise.all(fetchPromises);

    // Update domDataMap with fetched barcodes
    products.forEach(product => {
      if (!product) return;

      product.variants.forEach(variant => {
        const variantId = variant.id;
        if (!domDataMap[variantId]) {
          domDataMap[variantId] = {
            barcode: '',
            sku: '',
            discountAmount: 0,
            discountsJson: '',
            discountTitles: [],
            sortPriority: '',
            originalLinePrice: 0,
            finalLinePrice: 0
          };
        }

        // Update barcode and SKU from API
        if (variant.barcode) {
          domDataMap[variantId].barcode = variant.barcode;
        }
        if (variant.sku && !domDataMap[variantId].sku) {
          domDataMap[variantId].sku = variant.sku;
        }
      });
    });

    return domDataMap;
  }

  /**
   * Build discount codes array combining Shopify cart data and custom discount system
   */
  buildDiscountCodes(cart) {
    const codes = [];

    // Add codes from Shopify's cart.js (standard discount codes)
    // Note: Shopify's discount_codes only contains {code, applicable}, not amount
    // The amount needs to be calculated from cart.total_discount or item discounts
    if (cart.discount_codes && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(dc => {
        if (dc.code && dc.applicable && !codes.some(c => c.code === dc.code)) {
          // Calculate total amount for this discount code from item-level discounts
          let discountAmount = 0;
          cart.items?.forEach(item => {
            item.discounts?.forEach(d => {
              if (d.title?.toUpperCase() === dc.code.toUpperCase()) {
                discountAmount += d.amount || 0;
              }
            });
            // Also check line_level_discount_allocations
            item.line_level_discount_allocations?.forEach(d => {
              if (d.discount_application?.title?.toUpperCase() === dc.code.toUpperCase()) {
                discountAmount += d.amount || 0;
              }
            });
          });

          // Fallback to cart.total_discount if no item-level discounts found
          if (discountAmount === 0) {
            discountAmount = cart.total_discount || 0;
          }

          codes.push({
            code: dc.code,
            amount: discountAmount
          });
        }
      });
    }

    // Only add custom auto-discount if not already in codes from cart.js
    const customCode = this.getActiveCustomDiscountCode();
    if (customCode && !codes.some(c => c.code.toUpperCase() === customCode.toUpperCase())) {
      codes.push({
        code: customCode,
        amount: cart.total_discount || 0,
        source: 'auto_discount'
      });
    }

    return codes;
  }

  /**
   * Aggregate automatic (line-level) discounts from cart items into a summary.
   * Shopify automatic discounts like "Ab 4 Wänden 15%" are line-level, meaning
   * they only appear in item.discounts, NOT in cart_level_discount_applications.
   * This method aggregates them so they're visible at the top-level payload.
   */
  aggregateAutomaticDiscounts(cart) {
    const map = {};
    (cart.items || []).forEach(item => {
      // Use item.discounts (primary source)
      const discounts = item.discounts?.length > 0
        ? item.discounts
        : (item.line_level_discount_allocations || []).map(d => ({
            title: d.discount_application?.title,
            amount: d.amount || 0
          }));

      discounts.forEach(d => {
        const title = d.title;
        const amount = d.amount || 0;
        if (title && amount > 0) {
          if (!map[title]) map[title] = { title, total_amount: 0 };
          map[title].total_amount += amount;
        }
      });
    });
    return Object.values(map);
  }

  /**
   * Detect active custom discount code.
   *
   * Reads the discount code from cart drawer data attributes
   * based on the active assembly preference (button 1 or 2).
   */
  getActiveCustomDiscountCode() {
    try {
      const pref = localStorage.getItem('assemblyPreference') || '1';
      const codeAttr = pref === '2' ? 'data-montage-code' : 'data-eigenmontage-code';
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        const itemWithCode = cartDrawer.querySelector(`[${codeAttr}]:not([${codeAttr}=""])`);
        if (itemWithCode) {
          return itemWithCode.getAttribute(codeAttr);
        }
      }
      return null;
    } catch (e) {
      console.warn('[QuoteRequest] Failed to detect custom discount:', e);
      return null;
    }
  }

  /**
   * Show debug output before form submission (cart data only, no customer info)
   * Includes comparison between raw cart.js and transformed payload
   */
  async showPreSubmitDebug() {
    // Build payload with empty customer data (just for preview)
    const previewPayload = await this.buildPayload({}, this.cartData);

    // Remove customer data for preview (we only want to verify cart)
    delete previewPayload.customer;

    // Build comparison data
    const comparison = this.buildCartComparison(this.cartData, previewPayload);

    const debugContainer = document.getElementById('quote-request-debug');
    const debugOutput = document.getElementById('quote-request-debug-output');

    if (debugContainer && debugOutput) {
      debugContainer.style.display = 'block';
      debugOutput.innerHTML = this.formatDebugComparison(comparison, previewPayload);
    }

    // Also log to console
    console.group('📤 QUOTE REQUEST DEBUG (Pre-Submit Preview)');
    console.log('Raw cart.js:', JSON.parse(JSON.stringify(this.cartData)));
    console.log('Transformed payload:', JSON.parse(JSON.stringify(previewPayload)));
    console.log('Comparison:', comparison);
    console.groupEnd();
  }

  /**
   * Build comparison between raw cart.js and transformed payload
   */
  buildCartComparison(cart, payload) {
    const issues = [];
    const matches = [];

    // Compare totals
    if (cart.total_price === payload.cart.total_price) {
      matches.push(`✓ total_price: ${cart.total_price} (${this.formatMoney(cart.total_price)})`);
    } else {
      issues.push(`✗ total_price: cart=${cart.total_price} vs payload=${payload.cart.total_price}`);
    }

    if (cart.original_total_price === payload.cart.original_total_price) {
      matches.push(`✓ original_total_price: ${cart.original_total_price} (${this.formatMoney(cart.original_total_price)})`);
    } else {
      issues.push(`✗ original_total_price: cart=${cart.original_total_price} vs payload=${payload.cart.original_total_price}`);
    }

    if (cart.total_discount === payload.cart.total_discount) {
      matches.push(`✓ total_discount: ${cart.total_discount} (${this.formatMoney(cart.total_discount)})`);
    } else {
      issues.push(`✗ total_discount: cart=${cart.total_discount} vs payload=${payload.cart.total_discount}`);
    }

    if (cart.item_count === payload.cart.item_count) {
      matches.push(`✓ item_count: ${cart.item_count}`);
    } else {
      issues.push(`✗ item_count: cart=${cart.item_count} vs payload=${payload.cart.item_count}`);
    }

    // Compare items
    cart.items.forEach((cartItem, index) => {
      const payloadItem = payload.cart.items[index];
      if (!payloadItem) {
        issues.push(`✗ Item ${index}: missing in payload`);
        return;
      }

      const itemLabel = `Item ${index} (${cartItem.product_title.substring(0, 30)}...)`;

      // Price comparisons
      if (cartItem.final_line_price === payloadItem.final_line_price) {
        matches.push(`✓ ${itemLabel} final_line_price: ${cartItem.final_line_price}`);
      } else {
        issues.push(`✗ ${itemLabel} final_line_price: cart=${cartItem.final_line_price} vs payload=${payloadItem.final_line_price}`);
      }

      if (cartItem.original_line_price === payloadItem.original_line_price) {
        matches.push(`✓ ${itemLabel} original_line_price: ${cartItem.original_line_price}`);
      } else {
        issues.push(`✗ ${itemLabel} original_line_price: cart=${cartItem.original_line_price} vs payload=${payloadItem.original_line_price}`);
      }

      // Discount comparison
      const cartItemDiscount = cartItem.original_line_price - cartItem.final_line_price;
      if (cartItemDiscount === payloadItem.discount_amount) {
        matches.push(`✓ ${itemLabel} discount_amount: ${cartItemDiscount}`);
      } else {
        issues.push(`✗ ${itemLabel} discount_amount: calculated=${cartItemDiscount} vs payload=${payloadItem.discount_amount}`);
      }

      // Discounts array
      const cartDiscountsCount = cartItem.discounts?.length || 0;
      const payloadDiscountsCount = payloadItem.discounts?.length || 0;
      if (cartDiscountsCount === payloadDiscountsCount) {
        matches.push(`✓ ${itemLabel} discounts array count: ${cartDiscountsCount}`);
      } else {
        issues.push(`✗ ${itemLabel} discounts array: cart=${cartDiscountsCount} vs payload=${payloadDiscountsCount}`);
      }

      // Check sort_priority (info only, not a mismatch)
      if (payloadItem.sort_priority) {
        matches.push(`✓ ${itemLabel} sort_priority: "${payloadItem.sort_priority}"`);
      } else {
        matches.push(`ℹ ${itemLabel} sort_priority: (nicht gesetzt)`);
      }
    });

    return { issues, matches, allMatch: issues.length === 0 };
  }

  /**
   * Format debug comparison as HTML
   */
  formatDebugComparison(comparison, payload) {
    const statusIcon = comparison.allMatch ? '✅' : '⚠️';
    const statusText = comparison.allMatch ? 'ALLE WERTE STIMMEN ÜBEREIN' : 'ABWEICHUNGEN GEFUNDEN';

    let html = `<strong style="color: #60a5fa">📦 Version 2.2.0</strong>\n\n`;
    html += `<strong style="color: ${comparison.allMatch ? '#4ade80' : '#fbbf24'}">${statusIcon} ${statusText}</strong>\n\n`;

    if (comparison.issues.length > 0) {
      html += `<strong style="color: #f87171">── ABWEICHUNGEN (${comparison.issues.length}) ──</strong>\n`;
      comparison.issues.forEach(issue => {
        html += `<span style="color: #f87171">${this.escapeHtml(issue)}</span>\n`;
      });
      html += '\n';
    }

    html += `<strong style="color: #4ade80">── ÜBEREINSTIMMUNGEN (${comparison.matches.length}) ──</strong>\n`;
    comparison.matches.forEach(match => {
      html += `<span style="color: #4ade80">${this.escapeHtml(match)}</span>\n`;
    });

    html += `\n<strong style="color: #c29b58">── VOLLSTÄNDIGER PAYLOAD ──</strong>\n`;
    html += this.escapeHtml(JSON.stringify(payload, null, 2));

    return html;
  }

  showDebugOutput(payload) {
    const debugContainer = document.getElementById('quote-request-debug');
    const debugOutput = document.getElementById('quote-request-debug-output');

    if (debugContainer && debugOutput) {
      debugContainer.style.display = 'block';
      debugOutput.textContent = JSON.stringify(payload, null, 2);
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.theme?.moneyFormat) {
      return this.formatMoneyWithFormat(cents, window.theme.moneyFormat);
    }
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback: use active market currency
    const currency = window.shop_currency || 'CHF';
    const amount = (cents / 100).toFixed(2);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch(e) {
      return `${currency} ${amount}`;
    }
  }

  formatMoneyWithFormat(cents, format) {
    const value = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async sendWebhook(payload) {
    const webhookUrl = this.modal.dataset.webhookUrl;

    if (!webhookUrl) {
      console.warn('Quote Request: No webhook URL configured');
      return;
    }

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        mode: 'no-cors', // Allow cross-origin requests to webhooks
        body: JSON.stringify(payload)
      });
      console.log('Quote Request: Webhook sent successfully');
    } catch (error) {
      // Intentionally don't show error to user - UX first approach
      console.warn('Quote Request: Webhook failed', error);
    }
  }

  showSuccess() {
    const successTitle = this.modal.dataset.successTitle || 'Vielen Dank!';
    const successText = this.modal.dataset.successText || 'Deine Offertanfrage wurde erfolgreich übermittelt. Du erhältst deine Offerte in Kürze per E-Mail.';

    this.contentContainer.innerHTML = `
      <div class="quote-request-success">
        <div class="quote-request-success__icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
        <h2 class="quote-request-success__title">${this.escapeHtml(successTitle)}</h2>
        <p class="quote-request-success__text">${this.escapeHtml(successText)}</p>
        <button type="button" class="quote-request-success__close">Schliessen</button>
      </div>
    `;

    // Add close handler
    const closeBtn = this.contentContainer.querySelector('.quote-request-success__close');
    closeBtn?.addEventListener('click', () => this.close());
  }
}

// Initialize once - prevent double initialization
function initQuoteRequestModal() {
  if (window.quoteRequestModal) return; // Already initialized
  window.quoteRequestModal = new QuoteRequestModal();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuoteRequestModal);
} else {
  initQuoteRequestModal();
}
